import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import CompliancePanel from "@/components/CompliancePanel";
import { STATUS_LABELS } from "@/lib/constants";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Check, X, MessageSquare, FileText, Factory, Truck, CreditCard, Download } from "lucide-react";

const money = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);

export default function ApplicationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [app, setApp] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/applications/${id}`).then((r) => setApp(r.data)).catch((e) => toast.error(apiError(e))), [id]);
  useEffect(() => { load(); }, [load]);

  if (!app) return <div className="p-8 text-slate-500">Chargement...</div>;

  const setStatus = async (status, note) => {
    setBusy(true);
    try { await api.patch(`/applications/${id}/status`, { status, note }); toast.success("Statut mis à jour"); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const pay = async (payment_type) => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { order_id: app.order.id, payment_type, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  const isMairie = ["ADMIN_MAIRIE", "AGENT_MAIRIE"].includes(user.role);
  const isMerchant = user.role === "COMMERCANT";
  const c = app.characteristics || {};

  return (
    <div className="max-w-6xl">
      <button onClick={() => nav("/app")} className="text-sm text-slate-500 hover:text-[#000091] flex items-center gap-1 mb-4"><ArrowLeft size={16} /> Retour</button>
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-mono text-sm text-slate-500">{app.reference}</div>
          <h1 className="font-display font-black text-3xl tracking-tight">{app.commercial?.business_name || app.merchant_name}</h1>
          <div className="flex items-center gap-2 text-sm text-slate-500 mt-1"><MapPin size={14} /> {app.address?.street}, {app.address?.city}</div>
        </div>
        <StatusBadge status={app.status} className="text-sm px-4 py-2" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* details */}
          <div className="card-flat p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Caractéristiques de l'enseigne</div>
            <div className="grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm">
              <div className="flex justify-between border-b border-border py-1.5"><span className="text-slate-500">Type</span><b>{app.sign_type}</b></div>
              <div className="flex justify-between border-b border-border py-1.5"><span className="text-slate-500">Dimensions</span><b>{c.width_cm}×{c.height_cm} cm</b></div>
              <div className="flex justify-between border-b border-border py-1.5"><span className="text-slate-500">Surface</span><b>{c.surface_m2} m²</b></div>
              <div className="flex justify-between border-b border-border py-1.5"><span className="text-slate-500">Lumineuse</span><b>{c.luminous ? "Oui" : "Non"}</b></div>
              <div className="flex justify-between border-b border-border py-1.5"><span className="text-slate-500">Matériaux</span><b>{(c.materials || []).join(", ") || "—"}</b></div>
              <div className="flex justify-between border-b border-border py-1.5"><span className="text-slate-500">Couleurs</span><b>{(c.colors || []).join(", ") || "—"}</b></div>
              <div className="flex justify-between border-b border-border py-1.5"><span className="text-slate-500">Texte</span><b>{c.text || "—"}</b></div>
              <div className="flex justify-between border-b border-border py-1.5"><span className="text-slate-500">Fixation</span><b>{c.fixation || "—"}</b></div>
            </div>
            {app.facade_photos?.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mt-4">{app.facade_photos.map((p, i) => <img key={i} src={p} alt="façade" className="w-full h-20 object-cover border border-border" />)}</div>
            )}
          </div>

          {app.precheck && <CompliancePanel result={app.precheck} />}

          {/* quotes */}
          {app.quotes?.length > 0 && (
            <div className="card-flat p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4 flex items-center gap-2"><FileText size={14} /> Devis reçus</div>
              <div className="space-y-3">
                {app.quotes.map((q) => (
                  <div key={q.id} data-testid={`quote-${q.number}`} className="border border-border p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div><b>{q.manufacturer_name}</b> <span className="text-xs text-slate-500 font-mono">{q.number}</span></div>
                      <div className="text-xl font-black font-display">{money(q.total)}</div>
                    </div>
                    <div className="text-sm text-slate-500 grid grid-cols-2 gap-1 mb-2">
                      <span>Délai : {q.delay_days} j</span><span>Validité : {q.validity_days} j</span>
                      <span>HT : {money(q.subtotal)}</span><span>TVA : {q.tax_rate}%</span>
                    </div>
                    {q.conditions && <p className="text-xs text-slate-500 mb-2">{q.conditions}</p>}
                    {isMerchant && q.status === "SENT" && (
                      <button onClick={async () => { setBusy(true); try { await api.post(`/quotes/${q.id}/accept`); toast.success("Devis accepté, commande créée"); await load(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } }}
                        data-testid={`accept-quote-${q.number}`} disabled={busy} className="bg-[#000091] text-white px-4 py-2 text-sm font-semibold hover:bg-blue-900 transition-colors">Accepter ce devis</button>
                    )}
                    {q.status === "ACCEPTED" && <span className="text-emerald-700 text-sm font-semibold flex items-center gap-1"><Check size={14} /> Accepté</span>}
                    {q.status === "REJECTED" && <span className="text-slate-400 text-sm">Non retenu</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* order tracking + payment */}
          {app.order && (
            <div className="card-flat p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3 flex items-center gap-2"><Truck size={14} /> Commande {app.order.reference}</div>
              <div className="text-sm mb-3">Fabricant : <b>{app.order.manufacturer_name}</b> · Montant : <b>{money(app.order.amount)}</b> · Statut : <b>{STATUS_LABELS[app.order.status] || app.order.status}</b></div>
              {app.order.payment_status === "paid" ? (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2 text-sm flex items-center gap-2"><Check size={15} /> Payé intégralement</div>
              ) : (
                <div className="border-t border-border pt-3">
                  <div className="text-sm mb-2">
                    {app.order.payment_status === "partial" ? <>Acompte réglé : <b>{money(app.order.amount_paid)}</b> · Reste dû : <b>{money(app.order.amount - (app.order.amount_paid || 0))}</b></> : "Aucun paiement effectué."}
                  </div>
                  {isMerchant && (
                    <div className="flex flex-wrap gap-2">
                      {(!app.order.amount_paid) && (
                        <button onClick={() => pay("deposit")} disabled={busy} data-testid="pay-deposit-btn" className="bg-[#000091] text-white px-4 py-2 text-sm font-semibold hover:bg-blue-900 transition-colors flex items-center gap-2"><CreditCard size={15} /> Payer l'acompte (30%)</button>
                      )}
                      <button onClick={() => pay("full")} disabled={busy} data-testid="pay-full-btn" className="border border-[#000091] text-[#000091] px-4 py-2 text-sm font-semibold hover:bg-[#000091] hover:text-white transition-colors flex items-center gap-2"><CreditCard size={15} /> Payer {app.order.payment_status === "partial" ? "le solde" : "la totalité"}</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* inspections */}
          {app.inspections?.length > 0 && (
            <div className="card-flat p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Contrôles terrain</div>
              {app.inspections.map((ins) => (
                <div key={ins.id} className="border border-border p-4 mb-2">
                  <div className="flex justify-between mb-2"><b>Agent : {ins.agent_name}</b><StatusBadge status={ins.decision === "COMPLIANT" ? "COMPLIANT" : ins.decision === "NON_COMPLIANT" ? "NON_COMPLIANT" : "FIELD_INSPECTION"} /></div>
                  <div className="text-sm text-slate-600">Similarité visuelle : <b>{ins.ai_analysis?.visual_similarity}%</b> (confiance {ins.ai_analysis?.confidence})</div>
                  {ins.ai_analysis?.differences?.map((diff, i) => <div key={i} className="text-sm text-amber-700">⚠ {diff.label}</div>)}
                  {ins.comments && <p className="text-sm text-slate-500 mt-1">{ins.comments}</p>}
                  <button onClick={() => downloadFile(`/inspections/${ins.id}/report`, `rapport-${app.reference}.pdf`)} data-testid={`download-report-${ins.id}`} className="mt-3 inline-flex items-center gap-2 border border-[#000091] text-[#000091] px-3 py-1.5 text-sm font-semibold hover:bg-[#000091] hover:text-white transition-colors"><Download size={14} /> Rapport PDF</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* sidebar: actions + history */}
        <div className="space-y-6">
          {isMairie && (
            <div className="card-flat p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Instruction du dossier</div>
              <div className="space-y-2">
                {app.status === "SUBMITTED" && <button onClick={() => setStatus("UNDER_REVIEW")} disabled={busy} data-testid="btn-review" className="w-full border border-[#000091] text-[#000091] py-2.5 font-semibold hover:bg-[#000091] hover:text-white transition-colors">Prendre en instruction</button>}
                {["SUBMITTED", "UNDER_REVIEW", "MORE_INFORMATION_REQUIRED"].includes(app.status) && (
                  <>
                    <button onClick={() => setStatus("APPROVED", "Demande approuvée")} disabled={busy} data-testid="btn-approve" className="w-full bg-emerald-600 text-white py-2.5 font-semibold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"><Check size={16} /> Approuver</button>
                    <button onClick={() => setStatus("REJECTED", "Demande refusée")} disabled={busy} data-testid="btn-reject" className="w-full bg-[#E1000F] text-white py-2.5 font-semibold hover:bg-red-700 transition-colors flex items-center justify-center gap-2"><X size={16} /> Refuser</button>
                    <button onClick={() => setStatus("MORE_INFORMATION_REQUIRED", "Complément demandé")} disabled={busy} className="w-full border border-border py-2.5 font-semibold hover:border-[#000091] transition-colors flex items-center justify-center gap-2"><MessageSquare size={16} /> Demander un complément</button>
                  </>
                )}
                <button onClick={() => api.post(`/applications/${id}/precheck`).then(() => load())} className="w-full border border-border py-2 text-sm hover:border-[#000091] transition-colors">Relancer le pré-contrôle</button>
              </div>
            </div>
          )}

          <div className="card-flat p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Historique</div>
            <div className="space-y-3">
              {[...(app.status_history || [])].reverse().map((h, i) => (
                <div key={i} className="flex gap-3 text-sm">
                  <div className="w-2 h-2 bg-[#000091] mt-1.5 shrink-0" />
                  <div>
                    <div className="font-semibold">{STATUS_LABELS[h.status] || h.status}</div>
                    <div className="text-xs text-slate-500">{new Date(h.at).toLocaleString("fr-FR")} · {h.by_name}</div>
                    {h.note && <div className="text-xs text-slate-600">{h.note}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
